import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Target, 
  Eye, 
  Heart, 
  ArrowRight,
  Users,
  Globe,
  Trophy,
  BookOpen
} from "@phosphor-icons/react";

const stats = [
  { value: "25+", label: "Years of Excellence" },
  { value: "500+", label: "Published Articles" },
  { value: "50+", label: "Countries Reached" },
  { value: "1000+", label: "Expert Reviewers" }
];

const values = [
  {
    icon: Target,
    title: "Research Excellence",
    description: "Committed to publishing groundbreaking research that advances scientific knowledge and improves healthcare outcomes."
  },
  {
    icon: Eye,
    title: "Transparency",
    description: "Maintaining open and honest communication throughout the peer-review and publication process."
  },
  {
    icon: Heart,
    title: "Integrity",
    description: "Upholding the highest standards of publication ethics and research integrity in every manuscript we publish."
  },
  {
    icon: Users,
    title: "Collaboration",
    description: "Fostering global partnerships among researchers, institutions, and healthcare professionals."
  }
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-[#0F172A] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0D9488]/30 to-[#1E40AF]/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-[#0D9488] text-xs tracking-[0.2em] uppercase font-bold">
              About Us
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Chivo'] tracking-tight leading-none mt-4 mb-6">
              Advancing Medical & Scientific Knowledge
            </h1>
            <p className="text-lg text-[#CBD5E1] leading-relaxed">
              iScience Publishers is dedicated to the dissemination of high-quality, peer-reviewed 
              research that contributes to the global body of scientific knowledge.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section data-testid="mission-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="https://images.pexels.com/photos/3735711/pexels-photo-3735711.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Our Mission"
                className="w-full rounded-sm shadow-lg"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <span className="text-[#0D9488] text-xs tracking-[0.2em] uppercase font-bold">
                Our Mission
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A] font-['Chivo'] tracking-tight mt-4 mb-6">
                Empowering Researchers Worldwide
              </h2>
              <p className="text-[#334155] leading-relaxed mb-6">
                At iScience Publishers, our mission is to empower researchers, clinicians, and scholars 
                by offering a seamless publishing experience, global visibility, and rapid dissemination 
                of knowledge.
              </p>
              <p className="text-[#334155] leading-relaxed mb-6">
                We believe that accessible, high-quality research is essential for driving innovation 
                in healthcare and life sciences. Our rigorous peer-review process ensures that every 
                published article meets the highest standards of scientific integrity.
              </p>
              <p className="text-[#334155] leading-relaxed">
                Through our diverse portfolio of journals, we cover a broad spectrum of medical and 
                scientific disciplines, providing a platform for groundbreaking discoveries that 
                shape the future of healthcare.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section data-testid="stats-section" className="py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                data-testid={`stat-${index}`}
                className="text-center"
              >
                <div className="text-4xl sm:text-5xl font-bold text-[#0D9488] font-['Chivo']">
                  {stat.value}
                </div>
                <div className="text-sm text-[#64748B] mt-2">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section data-testid="vision-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <span className="text-[#0D9488] text-xs tracking-[0.2em] uppercase font-bold">
                Our Vision
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A] font-['Chivo'] tracking-tight mt-4 mb-6">
                Shaping the Future of Scientific Publishing
              </h2>
              <p className="text-[#334155] leading-relaxed mb-6">
                We envision a world where cutting-edge research is accessible to everyone, where 
                knowledge flows freely across borders, and where scientific discoveries translate 
                into tangible improvements in human health and well-being.
              </p>
              <p className="text-[#334155] leading-relaxed">
                By continuously innovating our publishing practices and embracing new technologies, 
                we strive to be at the forefront of scientific communication, bridging the gap 
                between researchers and the communities they serve.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="order-1 lg:order-2"
            >
              <img
                src="https://images.pexels.com/photos/3735715/pexels-photo-3735715.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Our Vision"
                className="w-full rounded-sm shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section data-testid="values-section" className="py-20 md:py-32 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#0D9488] text-xs tracking-[0.2em] uppercase font-bold">
              Our Values
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A] font-['Chivo'] tracking-tight mt-4">
              What Guides Us
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                data-testid={`value-${index}`}
                className="bg-white border border-[#E2E8F0] p-8 rounded-sm flex gap-6"
              >
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 bg-[#F0FDFA] rounded-sm flex items-center justify-center">
                    <value.icon size={28} className="text-[#0D9488]" weight="duotone" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-xl text-[#0F172A] font-['Chivo'] mb-2">
                    {value.title}
                  </h3>
                  <p className="text-[#64748B]">
                    {value.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Reach Section */}
      <section data-testid="global-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#0D9488] text-xs tracking-[0.2em] uppercase font-bold">
              Global Impact
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A] font-['Chivo'] tracking-tight mt-4">
              Connecting Researchers Worldwide
            </h2>
            <p className="text-[#64748B] mt-4 max-w-2xl mx-auto">
              Our journals are read and cited by researchers in over 50 countries, 
              making a global impact on scientific advancement.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
              className="text-center p-8"
            >
              <Globe size={48} className="text-[#0D9488] mx-auto mb-4" weight="duotone" />
              <h3 className="font-semibold text-xl text-[#0F172A] font-['Chivo'] mb-2">
                Global Distribution
              </h3>
              <p className="text-[#64748B]">
                Indexed in major international databases for maximum discoverability
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="text-center p-8"
            >
              <Trophy size={48} className="text-[#0D9488] mx-auto mb-4" weight="duotone" />
              <h3 className="font-semibold text-xl text-[#0F172A] font-['Chivo'] mb-2">
                Recognized Quality
              </h3>
              <p className="text-[#64748B]">
                Impact factors and citations that reflect our commitment to excellence
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="text-center p-8"
            >
              <BookOpen size={48} className="text-[#0D9488] mx-auto mb-4" weight="duotone" />
              <h3 className="font-semibold text-xl text-[#0F172A] font-['Chivo'] mb-2">
                Open Access Options
              </h3>
              <p className="text-[#64748B]">
                Making research accessible to everyone, everywhere
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-24 bg-gradient-to-r from-[#0D9488] to-[#1E40AF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white font-['Chivo'] tracking-tight mb-4">
              Join Our Publishing Community
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              Become part of a global network of researchers and contribute to scientific advancement.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/submit-manuscript"
                data-testid="about-submit-btn"
                className="inline-flex items-center justify-center gap-2 bg-white text-[#0D9488] hover:bg-[#F0FDFA] px-8 py-4 rounded-sm font-semibold transition-colors"
              >
                Submit Manuscript
                <ArrowRight size={20} weight="bold" />
              </Link>
              <Link
                to="/contact"
                data-testid="about-contact-btn"
                className="inline-flex items-center justify-center gap-2 border border-white/30 text-white hover:bg-white/10 px-8 py-4 rounded-sm font-semibold transition-colors"
              >
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
